import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useContext, useMemo, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {AsyncCreateAssignmentModal} from "@khan/assignments-async-loader";
import {useGuideCommandListener} from "@khan/events-guide";
import {
    filterFlaggedQuestionResponses,
    useGuideAudioController,
    useGuidePreferences,
    useGuideTermsMessages,
    useMobileIconMenu,
    useSendGuideEvent,
} from "@khan/guide-core";
import {
    isActivitiesPage,
    parseSetPromptVariablePayload,
    setPromptVariable,
} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {
    type ActionTypeParametersModal,
    type ActionTypeParametersModalAssignRecommendationsGroup,
    type AssignRecommendationsGroup,
    type ChatInputMetadata,
    type HideChatOptions,
    type InteractionID,
    type Message,
} from "@khan/guide-types";
import {getHideChatOptions} from "@khan/guide-utils";
import {doNotTranslate} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import {ChatInputUI} from "./chat-input/chat-input-ui.tsx";
import type {ChatInputComponent} from "./chat-input/chat-input.tsx";
import {Messages} from "./messages/messages.tsx";
import {PreviewModeContext} from "./PreviewModeContext.ts";
import {useTextToSpeechOnboarding} from "./text-to-speech/use-text-to-speech-onboarding.ts";
import {isTextToSpeechDisabledForPath} from "./text-to-speech/utils/is-text-to-speech-disabled-for-path.ts";

interface Props {
    conversationRecordedNotice?: React.ReactNode;
    disableSend: boolean;
    forceHideChatOptions?: HideChatOptions;
    expanded?: boolean;
    onMessageSend: (message: string, metadata: ChatInputMetadata) => unknown;
    onOpenFeedbackForm: (interactionId: InteractionID) => unknown;
    skipSpeechOnboarding?: boolean;
    ChatInputComponent?: ChatInputComponent;
}

/**
 * The chat interface.
 */
export const Chat = ({
    conversationRecordedNotice,
    disableSend,
    forceHideChatOptions,
    expanded,
    onMessageSend,
    onOpenFeedbackForm,
    skipSpeechOnboarding,
    ChatInputComponent,
}: Props): React.ReactElement => {
    const [messagesWithMathCheck, setMessagesWithMathCheck] = useState<
        Set<number>
    >(new Set());
    const {pathname} = useLocation();
    const isMobileIconMenuOpen = useMobileIconMenu();
    const sendGuideEvent = useSendGuideEvent();
    const unmodifiedConversation = useGuideState("conversation");
    const isLoading = useGuideState("isLoading");
    const cooldownStatus = useGuideState("cooldownStatus");
    const hasAcknowledgedGuide = useGuideState("hasAcknowledgedGuide");
    const {hasOnboardingBeenViewed, initiateOnboarding} =
        useTextToSpeechOnboarding();
    const {isTextToSpeechSupported, selectedVoice} = useGuideAudioController();
    const guideTermsMessages = useGuideTermsMessages();

    const [assignRecommendationsGroup, setAssignRecommendationsGroup] =
        React.useState<AssignRecommendationsGroup>(null);

    const [guideActions, setGuideActions] = React.useState<Message[]>([]);

    const {preferences} = useGuidePreferences();
    const dabEnabled =
        preferences.showDABs !== null ? preferences.showDABs : true; // Default to enabled (winning variant from A/B test)

    // TODO(KORE-403): Move this to use-make-streaming-handlers.ts and make
    // a dedicated stream signals for these "background" actions
    useGuideCommandListener(({name, content}) => {
        if (name === "guide-action") {
            const actionMessage = {
                // The horizontal ellipsis character (not three periods) is appended
                // onto the command's content. This unique character is used so that
                // the getGuideIconReaction function will display the thinking reaction.
                text: t`${content}…`,
                type: "incoming" as const,
                user: "Teacher" as const,
                uiOnly: true,
                serverOnly: false,
                interactionId: "",
            };
            const updatedGuideActions: any = [...guideActions, actionMessage];
            if (content === "Doing math") {
                const indexOfLastAction =
                    conversation.length + updatedGuideActions.length - 1;
                const updatedMathCheckSet = new Set([
                    ...Array.from(messagesWithMathCheck),
                    indexOfLastAction,
                ]);
                setMessagesWithMathCheck(updatedMathCheckSet);
            }
            setGuideActions(updatedGuideActions);
        }

        if (name === "conversation-test-case-response") {
            const actionMessage = {
                // The horizontal ellipsis character (not three periods) is appended
                // onto the command's content. This unique character is used so that
                // the getGuideIconReaction function will display the thinking reaction.
                text: doNotTranslate`Test case: ${content}`,
                type: "incoming" as const,
                user: "Teacher" as const,
                uiOnly: true,
                serverOnly: false,
                interactionId: "",
            };
            setGuideActions([...guideActions, actionMessage]);
        }

        if (name === "set-prompt-variable") {
            // Command that sets a prompt variable. The
            // payload is expected to be SetPromptVariablePayload.
            const payload = parseSetPromptVariablePayload(content);
            if (payload) {
                setPromptVariable(payload.name, payload.value);
            }
        }
    });

    React.useEffect(() => {
        // Remove the guide actions when the next message in
        // the conversation comes in.
        setGuideActions([]);
    }, [unmodifiedConversation.length]);

    const modifiedConversation = React.useMemo(() => {
        let filteredConversation: Message[];
        filteredConversation = [
            ...filterFlaggedQuestionResponses(
                // We don't want serverOnly messages.
                unmodifiedConversation.filter(({serverOnly}) => !serverOnly),
            ),
        ];
        if (!dabEnabled) {
            const lastMessage = {...filteredConversation.at(-1)};
            const actionBubblesLength = lastMessage?.actionBubbles?.length ?? 0;
            if (actionBubblesLength) {
                const filteredActionBubbles =
                    lastMessage?.actionBubbles?.filter(
                        ({isDynamic}) => !isDynamic,
                    ) ?? [];
                if (filteredActionBubbles.length !== actionBubblesLength) {
                    lastMessage.actionBubbles = filteredActionBubbles;
                    filteredConversation = [
                        ...filteredConversation.slice(0, -1),
                        lastMessage as Message,
                    ];
                }
            }
        }
        return [...filteredConversation, ...guideActions];
    }, [unmodifiedConversation, dabEnabled, guideActions]);

    const conversation = !hasAcknowledgedGuide
        ? guideTermsMessages
        : modifiedConversation;

    const hideChatOptions = React.useMemo(() => {
        return getHideChatOptions({
            forceHideChatOptions,
            pathname,
            cooldownStatus,
            filteredConversation: modifiedConversation,
        });
    }, [forceHideChatOptions, pathname, cooldownStatus, modifiedConversation]);

    // Log an error if chat is disabled
    // TODO: TUT-426: log only when disabled for mastery content reasons
    // and preferably add logging for other disabled reasons too
    React.useEffect(() => {
        if (hideChatOptions?.message && hasAcknowledgedGuide) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideBotErrorMessageReceived",
                eventSchemaVersion: 2,
                aiGuideThreadId: "",
                aiGuideNavigationId: "",
                aiGuideInteractionId: "",
                errorKind: "CHAT_DISABLED_MASTERY_CONTENT",
            });
        }
    }, [hideChatOptions?.message, hasAcknowledgedGuide]);

    React.useEffect(() => {
        // We only want to automatically show the text-to-speech onboarding
        // experience if the user has never seen it before, has not already
        // selected a voice (which can be done via settings), and only if the
        // user can interact with the chat input.
        if (
            isTextToSpeechSupported &&
            !hasOnboardingBeenViewed &&
            !hideChatOptions &&
            !isTextToSpeechDisabledForPath(pathname) &&
            selectedVoice === null &&
            !skipSpeechOnboarding &&
            !RequestInfo.isE2E
        ) {
            initiateOnboarding();
        }
    }, [
        isTextToSpeechSupported,
        hasOnboardingBeenViewed,
        hideChatOptions,
        pathname,
        selectedVoice,
        skipSpeechOnboarding,
        initiateOnboarding,
    ]);

    const openModal = useCallback(
        (modalID: string, parameters: ActionTypeParametersModal) => {
            switch (modalID) {
                case "assignRecommendationsGroup": {
                    const {
                        classroomDescriptor,
                        contentDescriptors,
                        studentKaids,
                        groupName,
                    } =
                        parameters as ActionTypeParametersModalAssignRecommendationsGroup;
                    setAssignRecommendationsGroup({
                        classroomDescriptor,
                        contentDescriptors: contentDescriptors.split(","),
                        studentKaids: studentKaids.split(","),
                        groupName,
                    });
                    break;
                }
            }
        },
        [],
    );

    const handleAssignRecommendationsSuccess = async () => {
        if (assignRecommendationsGroup) {
            await sendGuideEvent("create_assignments_success", {
                params: {
                    groupName: assignRecommendationsGroup.groupName,
                },
            });
        }
    };

    const handleAssignRecommendationsClose = () => {
        setAssignRecommendationsGroup(null);
    };

    const actionBubbleCallbacks = useMemo(() => {
        return {
            onMessageSend,
            openModal,
        };
    }, [openModal, onMessageSend]);

    const previewModeContextValue = useContext(PreviewModeContext);
    const isPreviewMode =
        previewModeContextValue !== undefined &&
        previewModeContextValue["isPreviewMode"];

    return (
        <>
            <View
                style={[
                    styles.messagesWrapper,
                    // Some activities never have chat input. In these cases, we
                    // want to center-align Khanmigo's initial message(s) to
                    // make it visually clearer that there's no normal chat
                    hideChatOptions?.type === "ActivityWithoutChat"
                        ? styles.centerAlign
                        : styles.bottomAlign,
                    expanded &&
                        isActivitiesPage(pathname) &&
                        styles.adjustPaddingForActivities,
                    isPreviewMode && styles.previewMode,
                ]}
            >
                {/* Show a spinner if the conversation is initializing */}
                {conversation.length === 0 && isLoading ? (
                    <View style={styles.spinner}>
                        <CircularSpinner
                            testId="guide-chat-spinner"
                            size="medium"
                        />
                    </View>
                ) : (
                    <Messages
                        messagesWithMathCheck={messagesWithMathCheck}
                        conversation={conversation}
                        hideChatOptions={hideChatOptions}
                        expanded={expanded}
                        actionBubbleCallbacks={actionBubbleCallbacks}
                        onOpenFeedbackForm={onOpenFeedbackForm}
                    />
                )}
            </View>
            {!hideChatOptions && (
                <View style={styles.footer}>
                    <ChatInputUI
                        disableSend={disableSend}
                        expanded={expanded}
                        onMessageSend={onMessageSend}
                        ChatInputComponent={ChatInputComponent}
                    />
                </View>
            )}
            {!isMobileIconMenuOpen && conversationRecordedNotice}
            {assignRecommendationsGroup && (
                <AsyncCreateAssignmentModal
                    classroomDescriptor={
                        assignRecommendationsGroup.classroomDescriptor
                    }
                    contentDescriptors={
                        assignRecommendationsGroup.contentDescriptors
                    }
                    studentKaids={assignRecommendationsGroup.studentKaids}
                    onSuccess={handleAssignRecommendationsSuccess}
                    onClose={handleAssignRecommendationsClose}
                />
            )}
        </>
    );
};

const styles = StyleSheet.create({
    messagesWrapper: {
        // position: relative property seems to be necessary to ensure iOS scroll behavior.
        position: "relative",
        height: "100%",
        width: "100%",
        paddingInlineStart: spacing.medium_16,
        paddingInlineEnd: spacing.medium_16,
        margin: "0 auto",
        boxSizing: "border-box",
        overflowY: "scroll",
    },
    adjustPaddingForActivities: {
        paddingInlineStart: 0,
        paddingInlineEnd: 0,
    },
    bottomAlign: {
        flexFlow: "column-reverse",
    },
    centerAlign: {
        justifyContent: "center",
    },
    footer: {
        minBlockSize: "fit-content",
    },
    spinner: {
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        maxHeight: "100%",
    },
    previewMode: {
        paddingInlineStart: 0,
        paddingInlineEnd: 0,
        // in preview mode, another component is handling the scroll behavior
        overflowY: "initial",
    },
});
